import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, ArrowRight, ShieldCheck } from 'lucide-react';
import GlassSelect from './GlassSelect';
import AnimatedCounter from './AnimatedCounter';

const PricingCalculator = () => {
  const [selectedService, setSelectedService] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');

  const pricingData = {
    'Ad Campaign': {
      packages: [
        { name: 'Starter (1 month)', price: 6000 },
        { name: 'Professional (3 months)', price: 15000 },
        { name: 'Enterprise (6 months)', price: 25000 },
        { name: 'Dynamic (12 months)', price: 50000 }
      ]
    },
    'Social Media Marketing': {
      packages: [
        { name: 'Basic (1 month)', price: 25000 },
        { name: 'Growth (3 months)', price: 55000 },
        { name: 'Premium (6 months)', price: 90000 },
        { name: 'Dynamic (12 months)', price: 120000 }
      ]
    },
    'Website Development': {
      packages: [
        { name: 'Landing Page', price: 20000 },
        { name: 'Business Website', price: 35000 },
        { name: 'E-commerce/Full Stack Platform', price: 150000 },
        { name: 'Android App Development', price: 250000 },
        { name: 'Business Automation Setup', price: 350000}
      ]
    },
    'WhatsApp Marketing': {
      packages: [
        { name: 'Starter (1 month)', price: 3000 },
        { name: 'Professional (3 months)', price: 7000 },
        { name: 'Enterprise (6 months)', price: 15000 }
      ]
    }
  };

  const getCurrentPrice = () => {
    if (!selectedService || !selectedPackage) return 0;
    const service = pricingData[selectedService];
    const pkg = service?.packages.find(p => p.name === selectedPackage);
    return pkg?.price || 0;
  };

  const totalCost = getCurrentPrice();
  const advanceAmount = Math.round(totalCost * 0.5);

  const scrollToLeadForm = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Trigger service pre-fill in the form
      setTimeout(() => {
        const serviceSelect = document.querySelector('select[name="service"]');
        if (serviceSelect) {
          serviceSelect.value = selectedService;
          serviceSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, 500);
    }
  };

  return (
    <section 
      id="calculator"
      className="py-16 sm:py-20 lg:py-24 bg-[#181F32] relative"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="text-center mb-12"
        >
          <span className="text-[#F3453F] font-bold text-sm tracking-wider uppercase font-sans mb-2 block">
            Transparent Pricing Engine
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white font-heading">
            Investment <span className="text-[#F3453F]">Calculator</span>
          </h2>
          <p className="text-base sm:text-lg text-white/80 font-sans">
            Calculate estimated costs and budget required for your digital growth plan.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 35, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.215, 0.61, 0.355, 1] }}
          className="glass-panel-white p-6 sm:p-10"
        >
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200/80">
            <div className="glass-icon-pod w-12 h-12 p-3">
              <Calculator size={26} />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-[#181F32] font-heading">
                Calculate Your Marketing Budget
              </h3>
              <p className="text-xs text-[#181F32]/70 font-sans font-medium">Instant custom package estimator</p>
            </div>
          </div>

          <div className="space-y-6 font-sans">
            {/* Service Selection */}
            <div>
              <label className="block text-sm font-bold mb-2 text-[#181F32]">
                Select Primary Service *
              </label>
              <GlassSelect
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setSelectedPackage('');
                }}
                placeholder="Choose a digital marketing service..."
                options={[
                  { value: 'Ad Campaign', label: 'Ad Campaign (Meta/Google Ads)' },
                  { value: 'Social Media Marketing', label: 'Social Media Marketing' },
                  { value: 'Website Development', label: 'Website & App Development' },
                  { value: 'WhatsApp Marketing', label: 'WhatsApp Marketing API' },
                ]}
              />
            </div>

            {/* Package Selection */}
            {selectedService && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <label className="block text-sm font-bold mb-2 text-[#181F32]">
                  Select Package Tier *
                </label>
                <GlassSelect
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  placeholder="Choose a duration/tier package..."
                  options={pricingData[selectedService].packages.map((pkg) => ({
                    value: pkg.name,
                    label: `${pkg.name} — ₹${pkg.price.toLocaleString('en-IN')}`,
                  }))}
                />
              </motion.div>
            )}

            {/* Price Display */}
            {totalCost > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-8 p-6 glass-panel p-6"
              >
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base sm:text-lg font-bold text-white">
                      Total Project Investment:
                    </span>
                    <span className="text-2xl sm:text-3xl font-extrabold text-[#F3453F] font-heading">
                      <AnimatedCounter value={`₹${totalCost.toLocaleString('en-IN')}`} />
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-white/20">
                    <span className="text-sm sm:text-base font-bold text-white">
                      Advance Payment (50% to Kickstart):
                    </span>
                    <span className="text-xl sm:text-2xl font-bold text-white font-heading">
                      <AnimatedCounter value={`₹${advanceAmount.toLocaleString('en-IN')}`} />
                    </span>
                  </div>
                  <div className="text-xs text-white/90 space-y-1.5 pt-2">
                    <p className="flex items-center gap-1.5 font-bold text-white">
                      <ShieldCheck size={16} className="text-[#F3453F]" />
                      50% advance required to initiate strategy & creative setup.
                    </p>
                    <p>• Remaining 50% payable upon project deployment or campaign phase milestone.</p>
                    <p>• All prices mentioned are exclusive of applicable taxes (GST).</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* CTA Button */}
            {totalCost > 0 && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={scrollToLeadForm}
                className="w-full mt-6 btn-glass btn-glass-submit py-4 text-base sm:text-lg"
              >
                Proceed & Request Custom Proposal
                <ArrowRight size={20} className="ml-2 inline-block" />
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingCalculator;
